<template>
  <div
    v-if="visible"
    class="btr"
    :class="{ 'btr--on': activo, 'btr--interruptor': parte === 'interruptor', 'btr--previa': parte === 'previa' }"
  >
    <!-- La fila del interruptor. En «interruptor» va en línea, en la misma fila
         que el IA/Manual: dice en qué idiomas se lee y se escribe sin gastar un
         renglón entero encima de la caja. -->
    <div v-if="parte !== 'previa'" class="btr__fila">
      <span class="btr__ico" aria-hidden="true">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/></svg>
      </span>

      <span class="btr__txt" :title="textoCompleto">
        <template v-if="!activo">
          <template v-if="parte === 'interruptor'">
            <template v-if="idiomaHuesped">{{ $t("mensajesChat.translate.guestWrites", { idioma: nombreHuesped }) }}</template>
          </template>
          <template v-else>
            {{ $t("mensajesChat.translate.off") }}
            <template v-if="idiomaHuesped">
              · {{ $t("mensajesChat.translate.guestWrites", { idioma: nombreHuesped }) }}
            </template>
          </template>
        </template>
        <!-- Encendido dice SIEMPRE en qué idioma saldrá lo escrito, también
             cuando el huésped escribe en el mismo que el panel: el idioma de
             salida se puede cambiar igual, como en el buzón del manager. -->
        <template v-else-if="hayQueLeer">
          {{ $t("mensajesChat.translate.youRead") }} <b>{{ miIdiomaNombre }}</b> ·
          {{ $t("mensajesChat.translate.willBeSentIn") }}
        </template>
        <template v-else>
          {{ conMayuscula($t("mensajesChat.translate.willBeSentIn")) }}
        </template>
      </span>

      <select
        v-if="activo"
        class="btr__sel"
        :value="salida"
        :title="$t('mensajesChat.translate.pickOutgoing')"
        @change="$emit('salida', $event.target.value)"
      >
        <option v-for="o in opciones" :key="o.cod" :value="o.cod">{{ o.etiqueta }}</option>
      </select>

      <button
        type="button"
        class="btr__sw"
        role="switch"
        :aria-checked="activo ? 'true' : 'false'"
        :title="activo ? $t('mensajesChat.translate.turnOff') : $t('mensajesChat.translate.turnOn')"
        @click="$emit('alternar')"
      >
        <span class="btr__sw__rot">{{ activo ? $t("mensajesChat.translate.on") : $t("mensajesChat.translate.translate") }}</span>
        <span class="btr__sw__via" aria-hidden="true"><i></i></span>
      </button>
    </div>

    <!-- La previa: lo que se va a enviar, en el idioma en que le llegará. Va
         encima de la caja de escribir, que es donde se está mirando. -->
    <template v-if="parte !== 'interruptor'">
      <div v-if="previa" class="btr__previa">
        <div class="btr__previa__cab">
          <span>{{ $t("mensajesChat.translate.willArriveAs", { idioma: previa.nombre }) }}</span>
          <button type="button" class="btr__previa__x" :title="$t('mensajesChat.translate.discard')" @click="$emit('descartar')">×</button>
        </div>
        <p class="btr__previa__txt">{{ previa.texto }}</p>
        <div class="btr__previa__acc">
          <button type="button" class="btr__previa__ok" @click="$emit('enviar')">
            {{ $t("mensajesChat.translate.sendIn", { idioma: previa.nombre }) }}
          </button>
          <button type="button" class="btr__previa__no" @click="$emit('descartar')">
            {{ $t("mensajesChat.translate.cancel") }}
          </button>
          <span class="btr__previa__tip">{{ $t("mensajesChat.translate.enterSends") }}</span>
        </div>
      </div>

      <p v-else-if="traduciendo" class="btr__esperando">
        <span class="btr__spin" aria-hidden="true"></span> {{ $t("mensajesChat.translate.translating") }}
      </p>
    </template>
  </div>
</template>

<script>
/**
 * La barra del traductor del chat: el interruptor, en qué idioma se lee y en
 * cuál saldrá lo que se escriba, y la previa antes de enviarlo.
 *
 * `parte` la parte en dos para que no ocupe un renglón propio:
 *   · "interruptor" → la fila en línea, para ponerla junto al IA/Manual;
 *   · "previa"      → solo la previa (y el «traduciendo…»), encima de la caja;
 *   · "todo"        → las dos juntas, como bloque.
 *
 * No sabe nada de ninguna pantalla: toda la lógica vive en el mixin
 * traductorDeChat, y aquí solo se pinta y se avisa hacia fuera.
 */
export default {
  name: "BarraTraduccion",
  props: {
    parte: { type: String, default: "todo" },
    activo: { type: Boolean, default: false },
    /** Código y nombre del idioma del huésped. */
    idiomaHuesped: { type: String, default: "" },
    nombreHuesped: { type: String, default: "" },
    /** El idioma en el que mira el anfitrión (el del panel). */
    miIdiomaNombre: { type: String, default: "" },
    /** El idioma en el que saldrá lo escrito, y las opciones del selector. */
    salida: { type: String, default: "" },
    opciones: { type: Array, default: () => [] },
    hayQueLeer: { type: Boolean, default: false },
    hayQueEnviar: { type: Boolean, default: false },
    previa: { type: Object, default: null },
    traduciendo: { type: Boolean, default: false },
  },
  methods: {
    conMayuscula(t) {
      const s = String(t || "");
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
  },
  computed: {
    /** La parte de la previa no ocupa nada mientras no haya nada que enseñar. */
    visible() {
      if (this.parte !== "previa") return true;
      return !!this.previa || this.traduciendo;
    },
    /** La frase entera, para el tooltip cuando en línea no cabe. */
    textoCompleto() {
      if (!this.activo) {
        return this.idiomaHuesped
          ? this.$t("mensajesChat.translate.off") + " · " + this.$t("mensajesChat.translate.guestWrites", { idioma: this.nombreHuesped })
          : this.$t("mensajesChat.translate.off");
      }
      return "";
    },
  },
};
</script>

<style scoped>
.btr {
  margin: 0 0 8px;
  padding: 7px 10px 7px 11px;
  border-radius: 11px;
  background: #f3f4f6;
  color: #6b7280;
}
/* Encendida se tiñe de violeta, el color con el que el panel habla de idioma. */
.btr--on { background: #f3eeff; color: #4c2bb3; }

/* En línea con el IA/Manual: sin caja, a la derecha, y el texto se recorta
   antes que empujar el interruptor fuera de la fila. */
.btr--interruptor {
  margin: 0 0 0 auto;
  padding: 0;
  background: transparent;
  min-width: 0;
  flex: 0 1 auto;
}
.btr--interruptor.btr--on { background: transparent; }
.btr--interruptor .btr__fila { gap: 8px; }
.btr--interruptor .btr__txt {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
}
.btr--interruptor .btr__sw { height: 28px; }

/* Solo la previa: sin el fondo gris de la fila, que ya no está. */
.btr--previa { padding: 0; background: transparent; }
.btr--previa .btr__previa { margin-top: 0; background: #f3eeff; }
.btr--previa .btr__esperando { margin: 0 0 2px; color: #4c2bb3; }

.btr__fila { display: flex; align-items: center; gap: 9px; min-width: 0; }
.btr__ico { display: flex; flex-shrink: 0; opacity: 0.85; }
.btr__txt { flex: 1 1 auto; min-width: 0; font-size: 12.5px; line-height: 1.35; }
.btr__txt b { font-weight: 600; }

.btr__sel {
  flex-shrink: 0;
  font-family: inherit; font-size: 12.5px; font-weight: 600; color: inherit;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(103, 61, 231, 0.22);
  border-radius: 7px; padding: 2px 4px 2px 6px; cursor: pointer; max-width: 160px;
}
.btr__sel:hover { border-color: rgba(103, 61, 231, 0.45); }

.btr__sw {
  display: inline-flex; align-items: center; gap: 8px; height: 30px;
  padding: 0 6px 0 11px; border: 0; border-radius: 999px; background: #e9ebef;
  font-family: inherit; font-size: 12.5px; font-weight: 700; color: #6b7280;
  cursor: pointer; flex-shrink: 0; transition: background 0.13s, color 0.13s;
}
.btr__sw:hover { background: #e1e4e8; color: #111827; }
.btr--on .btr__sw { background: #673de7; color: #fff; }
.btr--on .btr__sw:hover { background: #5a2fd6; }
.btr__sw__via { position: relative; width: 34px; height: 18px; border-radius: 999px; background: rgba(0, 0, 0, 0.18); flex-shrink: 0; }
.btr--on .btr__sw__via { background: rgba(255, 255, 255, 0.5); }
.btr__sw__via i { position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); transition: transform 0.15s; }
.btr--on .btr__sw__via i { transform: translateX(16px); }

.btr__previa { margin-top: 9px; background: rgba(255, 255, 255, 0.85); border: 1px solid rgba(103, 61, 231, 0.18); border-radius: 10px; padding: 9px 11px; color: #4c2bb3; }
.btr__previa__cab { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 11.5px; font-weight: 600; }
.btr__previa__x { border: 0; background: none; font-size: 17px; line-height: 1; color: inherit; opacity: 0.55; cursor: pointer; padding: 0 2px; }
.btr__previa__x:hover { opacity: 1; }
.btr__previa__txt { margin: 6px 0 9px; font-size: 13.5px; color: #111827; white-space: pre-wrap; word-break: break-word; }
.btr__previa__acc { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.btr__previa__ok, .btr__previa__no { font-family: inherit; font-size: 12.5px; font-weight: 600; border: 0; border-radius: 9px; padding: 6px 12px; cursor: pointer; }
.btr__previa__ok { background: #11b14d; color: #fff; }
.btr__previa__ok:hover { background: #0da847; }
.btr__previa__no { background: #e9ebef; color: #6b7280; }
.btr__previa__tip { font-size: 11px; opacity: 0.75; }

.btr__esperando { display: flex; align-items: center; gap: 7px; margin: 7px 0 0; font-size: 12px; }
.btr__spin { width: 12px; height: 12px; border: 2px solid rgba(0, 0, 0, 0.12); border-top-color: currentColor; border-radius: 50%; animation: btrGira 0.7s linear infinite; }
@keyframes btrGira { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .btr__sw, .btr__sw__via i { transition: none; }
  .btr__spin { animation-duration: 1.6s; }
}
</style>
